/**
 * PublicPrometPage — the public page »Promet po conah« (#867), /javno/promet/<app public_id>.
 *
 * MONG's public view of the zone module: the EnCLOD logo on top, a notice about
 * what is shown and the period limits, the panel's own zone view (public mode:
 * the summed categories, the weekday choice since #975) and a small map with
 * the panel's own zone overlay. No session, no login UI, no AI, no »Števci
 * prometa«. Everything is read from the public route family
 * (`/api/v1/public/promet/<id>/…`); a 404 there means this app has no public
 * page.
 *
 * The map is a plain MapLibre map on the King.Prostor basemap (the CDN style
 * without the cadastre layers, as on the tenant maps), opened on the app's
 * zones. If it cannot start (no WebGL), the figures still work without it.
 *
 * Plan: docs/plans/nova-gorica-pilot/promet-public-page-867.md
 */

import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import { EventBus } from '../utils/EventBus.js';
import { getDefaultBasemap, getResolvedStyleUrl, initServerEnv } from '../map/base/basemaps.js';
import {
  NgPrometZoneOverlay,
  zoneGeometryFromCollections,
} from '../map/unified/v6/components/NgPrometPanel/NgPrometZoneOverlay.js';
import { NgPrometZoneView } from '../map/unified/v6/components/NgPrometPanel/NgPrometZoneView.js';
import type { ZoneOverlayState } from '../map/unified/v6/components/NgPrometPanel/ngPrometZoneModel.js';
import { enclodLogoHtml } from '../map/unified/v6/components/ngEnclodLogo/ngEnclodLogo.js';
import { BRAND } from '../../shared/utils/brand.js';
import {
  PUBLIC_PROMET_LIMITS,
  publicPrometApiBase,
  type PublicPrometGeometry,
} from '../../shared/utils/ngPrometPublic.js';
import {
  PAGE_COPY,
  appIdFromPath,
  basemapCredits,
  geometryBounds,
  pageFailureText,
  pagePath,
} from './publicPrometPageModel.js';

const COPY = {
  title: 'Promet po conah',
  mapFailed: 'Zemljevida ni bilo mogoče naložiti. Podatki spodaj so na voljo tudi brez njega.',
};

export class PublicPrometPage {
  private map: MlMap | null = null;

  private el<T extends HTMLElement>(role: string): T {
    const node = document.querySelector<T>(`[data-role="${role}"]`);
    if (!node) throw new Error(`[javno-promet] [data-role="${role}"] missing`);
    return node;
  }

  async start(): Promise<void> {
    // The EnCLOD logo is on top of every view of the module, the notice with it (MONG: say the limits on the page).
    this.el('logo').innerHTML = enclodLogoHtml({ eager: true });
    this.el('notice').textContent = PAGE_COPY.notice(PUBLIC_PROMET_LIMITS);

    const appId = appIdFromPath(window.location.pathname);
    if (!appId) {
      this.fail(PAGE_COPY.unavailable);
      return;
    }
    // One canonical address (a trailing slash is accepted, not kept).
    if (window.location.pathname !== pagePath(appId)) {
      history.replaceState(history.state, '', pagePath(appId) + window.location.search + window.location.hash);
    }

    const base = publicPrometApiBase(appId);
    let geometry: PublicPrometGeometry;
    try {
      const resp = await fetch(`${base}/geometry`);
      if (!resp.ok) {
        console.warn('[javno-promet] geometry request failed', resp.status);
        this.fail(pageFailureText(resp.status));
        return;
      }
      geometry = (await resp.json()) as PublicPrometGeometry;
    } catch (err) {
      console.error('[javno-promet] geometry request threw', err);
      this.fail(PAGE_COPY.failed);
      return;
    }

    this.el('subtitle').textContent = geometry.name;
    document.title = `${COPY.title} · ${geometry.name} — ${BRAND.name}`;
    this.el('state').hidden = true;
    this.el('main').hidden = false;

    await this.startMap(geometry);

    const bus = new EventBus();
    const zoneGeometry = zoneGeometryFromCollections(geometry.zones, geometry.lines, geometry.points);
    const overlay = new NgPrometZoneOverlay(
      { getMap: () => this.map },
      bus,
      () => appId,
      () => true,
      () => Promise.resolve(zoneGeometry),
    );
    const view = new NgPrometZoneView(
      this.el('view'),
      bus,
      { sxappId: appId },
      { zonesPath: `${base}/zones`, limits: PUBLIC_PROMET_LIMITS },
    );

    // The map page's wiring of the same events (UnifiedUIManager, NgPrometPanel).
    bus.on('ng:promet-zone-overlay', ({ state }: { state: ZoneOverlayState | null }) => {
      void overlay.apply(state);
    });
    bus.on('ng:promet-zone-fit', () => {
      // Wherever the map is not fully on screen — the stacked layout of a phone,
      // a tablet or a narrow window puts it above the figures — bring it into
      // view first. The sticky map of the wide layout is already there.
      const box = this.el('mapbox');
      const r = box.getBoundingClientRect();
      if (r.top < 0 || r.bottom > document.documentElement.clientHeight) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        box.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
      void overlay.fit();
    });
    bus.on('ng:promet-zone-point', ({ accessPointId }: { accessPointId: number }) => {
      view.selectPoint(accessPointId, { fromMap: true });
    });

    view.show();
  }

  /** The map, opened on the app's zones; on failure the page goes on without it. */
  private async startMap(geometry: PublicPrometGeometry): Promise<void> {
    const box = this.el('mapbox');
    let map: MlMap | null = null;
    try {
      // Picks the dev / prod style build (basemaps.ts); a failed call means prod.
      await initServerEnv();
      const style = getResolvedStyleUrl(getDefaultBasemap(), { useGursOffVariant: true });
      if (!style) throw new Error('no basemap style');
      const bounds = geometryBounds(geometry);
      map = new maplibregl.Map({
        container: this.el('map'),
        style,
        ...(bounds
          ? { bounds, fitBoundsOptions: { padding: 32, maxZoom: 15 } }
          : { center: [13.65, 45.95] as [number, number], zoom: 12 }),
        maxZoom: 19,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
      });
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      const m = map;
      await new Promise<void>((resolve, reject) => {
        // A tile, glyph or source error is not a failed start; only a style that
        // never loads is. (`isStyleLoaded()` cannot tell: it stays false while
        // any tile is still loading.)
        let styleReady = false;
        void m.once('style.load', () => {
          styleReady = true;
        });
        const onError = (e: { error?: Error; sourceId?: string }): void => {
          if (styleReady || e.sourceId) return;
          m.off('error', onError);
          reject(e.error ?? new Error('map error'));
        };
        m.on('error', onError);
        void m.once('load', () => {
          m.off('error', onError);
          resolve();
        });
      });
      // The basemap credit sits on a style source no layer draws (publicPrometPageModel.ts).
      m.addControl(
        new maplibregl.AttributionControl({
          compact: true,
          customAttribution: basemapCredits(m.getStyle() as unknown as Parameters<typeof basemapCredits>[0]),
        }),
        'bottom-right',
      );
      this.map = m;
    } catch (err) {
      console.error('[javno-promet] map failed', err);
      map?.remove();
      this.map = null;
      box.classList.add('is-failed');
      box.textContent = COPY.mapFailed;
    }
  }

  private fail(message: string): void {
    this.el('main').hidden = true;
    const state = this.el('state');
    state.hidden = false;
    state.textContent = message;
    state.classList.add('is-error');
  }
}
